import { useRouteMatch } from 'react-router';

import PropTypes from 'prop-types';

import { Box, Button, SvgIcon, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

import { useTransl } from 'shared/lib/i18n';

import AmenityReservationsContainer from './AmenityReservationsContainer';

import BackButtonComponent from '../Forms/BackButtonComponent';
import ExpandableImageComponent from '../Image/ExpandableImageComponent';

import { isAdmin, isPM, toSentenceCase } from '../../utils';
import {
  formatAmenityReservationTime,
  formatTimeRange,
  getAmenityBaseUrl,
} from './utils';

import { buildingIcon } from '../../res/svg/icons';
import { history } from '../../store';
import WithLink from '../Linkify';
import StyledLink from '../StyledLink';

const StyledBodyText = styled(Typography)(() => ({
  fontSize: '1.6rem',
  whiteSpace: 'break-spaces',
  padding: 0,
  margin: 0,
}));

const StyledSubHeader = styled(Typography)(({ theme }) => ({
  fontSize: '1.8rem',
  fontWeight: theme.typography.fontWeightBold,
  [theme.breakpoints.down('md')]: {
    fontSize: '1.6rem',
  },
  padding: 0,
  margin: 0,
}));

const ScheduleRow = ({ schedule, day }) => {
  if (schedule?.[day]?.length < 1) return null;

  return (
    <Box sx={{ display: 'flex', fontSize: '1.6rem' }}>
      <Box width="12rem">{toSentenceCase(day)}</Box>
      <Box display="flex">
        {schedule?.[day].map(({ startTime, endTime }) => {
          const key = `${day}-${startTime}-${endTime}`;
          const start = formatTimeRange(startTime, 'hh:mma');
          const end = formatTimeRange(endTime, 'hh:mma');
          return <Box key={key} mr="1rem">{`${start} - ${end}`}</Box>;
        })}
      </Box>
    </Box>
  );
};
ScheduleRow.propTypes = {
  schedule: PropTypes.objectOf(PropTypes.instanceOf(Array)).isRequired,
  day: PropTypes.string.isRequired,
};

const InfoRowComponent = ({ title, text }) => (
  <Box>
    <StyledSubHeader component="span">{title}</StyledSubHeader>
    <Box component="span" mr="1rem" />
    <StyledBodyText component="span">{text}</StyledBodyText>
  </Box>
);
InfoRowComponent.propTypes = {
  title: PropTypes.string.isRequired,
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

const AmenityInfoComponent = (props) => {
  const { url } = useRouteMatch();
  const { t } = useTransl();
  const {
    id,
    images,
    name,
    building,
    schedule,
    minimumReservationTime,
    maximumReservationTime,
    minimumDaysBeforeReservation,
    maximumDaysBeforeReservation,
    ownerNumReservationsCount,
    tenantNumReservationsCount,
    simultaneousNumberOfReservations,
    notes,
    preselectedAmenityId,
    active,
    timezoneName,
    buildingType,
    upcomingReservationsCount,
    onRemoveAmenity,
    match,
    rentalAgreementRequired,
    rentalAgreement,
    requiresApproval,
    doors,
  } = props;

  const baseUrl = getAmenityBaseUrl(url);
  const isAvailable = Object.values(schedule)?.flat()?.length > 0;
  const limitSettingsData = [
    {
      title: t('title.minimumReservationTime'),
      text: formatAmenityReservationTime(minimumReservationTime),
    },
    {
      title: t('title.maximumReservationTime'),
      text: formatAmenityReservationTime(maximumReservationTime),
    },
    {
      title: t('title.minimumDaysBeforeReservation'),
      text: minimumDaysBeforeReservation,
    },
    {
      title: t('title.maximumDaysBeforeReservation'),
      text: maximumDaysBeforeReservation,
    },
    {
      title: t('title.ownerNumReservationsCount'),
      text: ownerNumReservationsCount,
      hide: buildingType !== 'condo',
    },
    {
      title: t('title.tenantNumReservationsCount'),
      text: tenantNumReservationsCount,
    },
    {
      title: t('title.simultaneousNumberOfReservations'),
      text: simultaneousNumberOfReservations,
    },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {preselectedAmenityId && (
        <StyledSubHeader>{t('title.amenityDetails')}</StyledSubHeader>
      )}
      {!!images?.[0] && (
        <ExpandableImageComponent photo={images?.[0]} caption={name} />
      )}
      {preselectedAmenityId ? null : (
        <Typography
          variant="h5"
          sx={(theme) => ({
            margin: '1.5rem 0 3rem 0',
            color: theme.palette.primary.main,
          })}
        >
          {name}
        </Typography>
      )}

      {!preselectedAmenityId && (
        <Box display="flex">
          <SvgIcon
            viewBox="0 0 32 32"
            sx={{ fontSize: '3rem', textAlign: 'center' }}
          >
            {buildingIcon}
          </SvgIcon>

          <Box
            sx={(theme) => ({
              color: theme.palette.primary.main,
              fontSize: '1.8rem',
              ml: '1rem',
            })}
          >
            <StyledLink to={`/building/${building?.id}`} color="inherit">
              {building?.name}
            </StyledLink>
          </Box>
        </Box>
      )}

      <Box sx={{ mt: '3rem', mb: '1rem' }}>
        <InfoRowComponent
          title={`${t('title.availability')}*:`}
          text={active ? t('amenity.active') : t('amenity.inactive')}
        />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {isAvailable ? (
          Object.keys(schedule).map((day) => (
            <ScheduleRow key={day} schedule={schedule} day={day} />
          ))
        ) : (
          <StyledBodyText>{t('amenity.notAvailable')}</StyledBodyText>
        )}
      </Box>
      {timezoneName && (
        <Box mt="1rem">
          &#42; {`${t('amenity.timezoneInfo')} ${timezoneName}`}
        </Box>
      )}

      <Box display="flex" mt="2rem" gap="1rem" flexWrap="wrap">
        <Typography fontWeight="bold">{t('title.connectedDoors')}:</Typography>
        {doors?.length > 0
          ? doors?.map(({ name, id }, i) => {
              const isNotLast = i + 1 < doors?.length;
              const doorUrl = `/building/${building?.id}/doors/${id}`;
              return (
                <StyledLink
                  key={id}
                  to={doorUrl}
                  sx={{ fontWeight: 'bold', color: 'inherit' }}
                >
                  {name}
                  {isNotLast && ','}
                </StyledLink>
              );
            })
          : t('common.NA')}
      </Box>

      <Box sx={{ mt: '3rem', mb: '1rem' }}>
        {limitSettingsData
          .filter(({ hide }) => !hide)
          .map(({ title, text }) => (
            <InfoRowComponent key={title} title={`${title}:`} text={text} />
          ))}
      </Box>

      {notes && (
        <>
          <StyledSubHeader sx={{ mt: '3rem', mb: '1rem' }}>
            {t('amenity.amenityNotes')}
          </StyledSubHeader>
          <StyledBodyText>
            <WithLink>{notes}</WithLink>
          </StyledBodyText>
        </>
      )}

      {requiresApproval && (
        <StyledSubHeader sx={{ mt: '3rem', mb: '1rem' }}>
          {t('amenity.requiresStaffApproval')}
        </StyledSubHeader>
      )}

      {rentalAgreementRequired && (
        <>
          <StyledSubHeader sx={{ mt: '3rem', mb: '1rem' }}>
            {t('amenity.rentalAgreement')}
          </StyledSubHeader>
          <Box
            sx={{
              fontSize: '1.6rem',
              whiteSpace: 'break-spaces',
              fontWeight: 'bold',
            }}
          >
            {rentalAgreement?.title}
          </Box>
          <StyledBodyText>
            <WithLink>{rentalAgreement?.text}</WithLink>
          </StyledBodyText>
        </>
      )}

      {!preselectedAmenityId && (
        <Box sx={{ display: 'flex', marginTop: '1.5rem' }}>
          <BackButtonComponent />
          <Box ml="2rem" />
          {(isAdmin() || isPM(match?.params)) && (
            <>
              <Button
                variant="contained"
                disableElevation
                color="primary"
                sx={{ width: '15rem' }}
                onClick={() => history.push(`${baseUrl}/management/${id}`)}
              >
                {t('actions.edit')}
              </Button>
              <Box ml="2rem" />
              <Button
                variant="contained"
                disableElevation
                color="secondary"
                onClick={() =>
                  onRemoveAmenity(id, name, upcomingReservationsCount)
                }
              >
                {t('actions.delete')}
              </Button>
            </>
          )}
        </Box>
      )}

      {!preselectedAmenityId && (
        <>
          <StyledSubHeader sx={{ mt: '3.5rem' }}>
            {t('amenity.reservationHistory')}
          </StyledSubHeader>
          <AmenityReservationsContainer
            amenityId={id}
            match={match}
            isReservationHistory
          />
        </>
      )}
    </Box>
  );
};

AmenityInfoComponent.defaultProps = {
  images: [],
  minimumReservationTime: '',
  maximumReservationTime: '',
  minimumDaysBeforeReservation: null,
  maximumDaysBeforeReservation: null,
  simultaneousNumberOfReservations: null,
  ownerNumReservationsCount: null,
  tenantNumReservationsCount: null,
  preselectedAmenityId: null,
  notes: '',
  buildingType: '',
};

AmenityInfoComponent.propTypes = {
  id: PropTypes.number.isRequired,
  images: PropTypes.arrayOf(PropTypes.instanceOf(Object)),
  name: PropTypes.string.isRequired,
  building: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
  schedule: PropTypes.objectOf(PropTypes.instanceOf(Array)).isRequired,
  minimumReservationTime: PropTypes.string,
  maximumReservationTime: PropTypes.string,
  minimumDaysBeforeReservation: PropTypes.number,
  maximumDaysBeforeReservation: PropTypes.number,
  simultaneousNumberOfReservations: PropTypes.number,
  ownerNumReservationsCount: PropTypes.number,
  tenantNumReservationsCount: PropTypes.number,
  notes: PropTypes.string,
  preselectedAmenityId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  active: PropTypes.bool.isRequired,
  timezoneName: PropTypes.string.isRequired,
  buildingType: PropTypes.string,
  upcomingReservationsCount: PropTypes.number.isRequired,
  onRemoveAmenity: PropTypes.func.isRequired,
  match: PropTypes.instanceOf(Object).isRequired,
  rentalAgreementRequired: PropTypes.bool.isRequired,
  rentalAgreement: PropTypes.instanceOf(Object),
  requiresApproval: PropTypes.bool.isRequired,
  doors: PropTypes.instanceOf(Array).isRequired,
};

export default AmenityInfoComponent;
